/**
 * FILE: apps/hub/src/pages/Login.tsx
 * PURPOSE: Hub page component.
 * AGENT_CONTEXT: Route-level component. Lazy-load if >200 lines expected.
 * INVARIANTS: Use QK.* query keys, useActor() for writes, theme tokens only.
 * LAST_REVIEWED: 2026-05-07
 */

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Navigate, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { signIn } from '@/lib/auth'
import { useAuth } from '@/hooks/useAuth'
import { useAuthStore } from '@/store/auth-store'
import { defaultPathByPermissions } from '@/lib/authz'
import { OnboardingShell } from '@/components/OnboardingShell'

const BLUE_LOGO = '/branding/logo-blue.png'

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

type LoginInput = z.infer<typeof loginSchema>

const inputClass =
  'border-border/ bg-card focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-fintree-royal/20'

function cardClassName() {
  return 'w-full max-w-sm rounded-2xl border border-border/ bg-card/95 p-10 shadow-login-v2 backdrop-blur-sm'
}

export function LoginPage() {
  const navigate = useNavigate()
  const { identity, status } = useAuth()
  const setIdentity = useAuthStore((state) => state.setIdentity)

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  if (status === 'loading') {
    return (
      <OnboardingShell>
        <main className={cardClassName()}>
          <div className="flex min-h-[200px] items-center justify-center">
            <LoadingSpinner label="Checking session..." />
          </div>
        </main>
      </OnboardingShell>
    )
  }

  if (identity) {
    return <Navigate to={defaultPathByPermissions(identity.permissions, identity.roleName)} replace />
  }

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const identity = await signIn(values.email, values.password)
      setIdentity(identity)
      navigate(defaultPathByPermissions(identity.permissions, identity.roleName), { replace: true })
    } catch (error) {
      form.setError('root', { message: error instanceof Error ? error.message : 'Unable to login.' })
    }
  })

  return (
    <OnboardingShell>
      <main className={cardClassName()}>
        <div className="mb-8 flex flex-col items-center text-center">
          <img
            src={BLUE_LOGO}
            alt="FinTree"
            className="mb-6 h-14 w-auto object-contain"
            width="200"
            height="56"
          />
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground">FinTree Hub</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Internal operations</p>
        </div>
        <form onSubmit={onSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-foreground">
              Email
            </Label>
            <Input id="email" type="email" className={inputClass} {...form.register('email')} />
            {form.formState.errors.email?.message ? (
              <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-foreground">
              Password
            </Label>
            <Input id="password" type="password" className={inputClass} {...form.register('password')} />
            {form.formState.errors.password?.message ? (
              <p className="text-xs text-destructive">{form.formState.errors.password.message}</p>
            ) : null}
          </div>
          {form.formState.errors.root?.message ? (
            <p className="text-xs text-destructive">{form.formState.errors.root.message}</p>
          ) : null}
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="h-11 w-full rounded-xl bg-primary font-medium text-white transition-colors hover:bg-primary/90"
          >
            {form.formState.isSubmitting ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      </main>
    </OnboardingShell>
  )
}
